import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Anchor, Building2, ArrowRight, ChevronRight, Gem, ShieldCheck, MapPin, Headphones, Sparkles, Clock } from "lucide-react";
import api from "../lib/api";
import { SearchBar } from "../components/SearchBar";
import { ListingCard } from "../components/ListingCard";
import { useI18n } from "../context/I18nContext";

const HERO_BG =
  "/media/riviera-background.jpeg";

export default function Home() {
  const { t } = useI18n();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/listings", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 6))).catch(() => {});
  }, []);

  const CATS = [
    { to: "/voitures", icon: Car, label: t("nav.cars"), sub: t("home.catCarsSub"), img: "https://yuazrortedwpauiophfn.supabase.co/storage/v1/object/public/vehicle_images/1777212951308-Photo-09-04-2026-13-59-32.jpg" },
    { to: "/yachts", icon: Anchor, label: t("nav.yachts"), sub: t("home.catYachtsSub"), img: "https://images.unsplash.com/photo-1597609490757-5fc92ead5cb1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
    { to: "/villas", icon: Building2, label: t("nav.villas"), sub: t("home.catVillasSub"), img: "https://images.unsplash.com/photo-1693934304978-22274e6a3276?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  ];

  const FEATURES = [
    { icon: Gem, label: t("home.f1") },
    { icon: ShieldCheck, label: t("home.f2") },
    { icon: MapPin, label: t("home.f3") },
    { icon: Headphones, label: t("home.f4") },
  ];

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover gz-zoom" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/45 to-[#05060A]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05060A] via-transparent to-black/70" />
        </div>

        <div className="relative w-full max-w-[1200px] mx-auto px-4 sm:px-8 pt-24 sm:pt-28 pb-10 flex flex-col items-center text-center">
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1 }}>
            <img src="/gl-medallion.png" alt="GROZNY LOCATION" className="h-40 sm:h-52 lg:h-60 w-auto object-contain drop-shadow-[0_0_45px_rgba(212,175,55,0.4)]" draggable="false" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-7 text-[#E9DCBB] text-sm sm:text-lg tracking-[0.35em] uppercase"
          >
            {t("home.tagline2")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-8 sm:mt-10 flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-10 gap-y-6"
          >
            {FEATURES.map((f, i) => (
              <div key={i} className="flex flex-col items-center gap-2 w-[130px] sm:w-[150px]">
                <f.icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
                <span className="text-white/85 text-[11px] sm:text-xs tracking-[0.18em] uppercase leading-relaxed">{f.label}</span>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.55 }} className="mt-9 sm:mt-12">
            <Link
              to="/voitures"
              data-testid="hero-cta-discover"
              className="group inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/70 bg-black/30 backdrop-blur-md text-[#F5EFD8] px-8 py-4 text-xs sm:text-sm tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-black transition-all duration-400"
            >
              {t("home.ctaDiscover")}
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>

        {/* Side accents */}
        <div className="hidden lg:block absolute bottom-10 left-12 text-left">
          <div className="font-display text-2xl tracking-[0.2em] text-[#F5EFD8]">{t("home.accTL")}</div>
          <div className="gz-hairline w-16 my-2" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 leading-relaxed">{t("home.accBL")}</div>
        </div>
        <div className="hidden lg:block absolute bottom-10 right-12 text-right">
          <div className="text-[10px] tracking-[0.35em] uppercase text-white/70 leading-loose">
            {t("home.accR1")}<br />{t("home.accR2")}<br />{t("home.accR3")}
          </div>
        </div>

        {/* Search bar docked at the bottom */}
        <div className="relative w-full max-w-5xl mx-auto px-4 sm:px-8 pb-12 -mt-2">
          <SearchBar />
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATS.map((c, i) => (
            <motion.div
              key={c.to}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <Link to={c.to} data-testid={`home-cat-${c.to.replace("/", "")}`} className="group relative block h-[380px] rounded-[24px] overflow-hidden border border-[#D4AF37]/15 shadow-[0_10px_40px_rgba(0,0,0,0.5)]">
                <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <div className="w-12 h-12 rounded-2xl bg-black/30 backdrop-blur-md border border-[#D4AF37]/30 flex items-center justify-center mb-4">
                    <c.icon className="w-6 h-6 text-[#E5C378]" />
                  </div>
                  <h3 className="font-display text-3xl text-white">{c.label}</h3>
                  <p className="text-white/80 text-sm mt-1">{c.sub}</p>
                  <span className="inline-flex items-center gap-2 text-[#E5C378] text-xs tracking-widest uppercase mt-4 group-hover:gap-3 transition-all">
                    {t("common.explore")} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 pb-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[11px] tracking-luxe uppercase text-[#D4AF37] mb-3">{t("home.featuredLabel")}</p>
              <h2 className="font-display text-4xl sm:text-5xl text-[#F5F0E1]">{t("home.featuredTitle")}</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((item, i) => (
              <ListingCard key={item.id} item={item} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Values */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 pb-24">
        <div className="gz-card rounded-[28px] p-10 sm:p-14 grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { icon: Sparkles, t: t("home.v1t"), d: t("home.v1d") },
            { icon: ShieldCheck, t: t("home.v2t"), d: t("home.v2d") },
            { icon: Clock, t: t("home.v3t"), d: t("home.v3d") },
          ].map((v) => (
            <div key={v.t} className="text-center md:text-left">
              <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/12 flex items-center justify-center mx-auto md:mx-0 mb-5">
                <v.icon className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-display text-2xl text-[#F5F0E1] mb-2">{v.t}</h3>
              <p className="text-[#93A099] text-sm leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
